import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

interface Props {
  personaId: string;
  persona: any;
}

const RevisionLog: React.FC<Props> = ({ personaId, persona }) => {
  const { darkMode } = useTheme();

  const ep = persona?.data?.data?.engineering_profile || {};
  const cs = ep?.current_state || {};
  const revision = cs.revision || 0;
  const lastModified = ep.last_modified;

  // Build revision entries from what we have (only latest revision is known — no versioning table yet)
  const entries = [];
  if (revision > 0) {
    entries.push({
      rev: revision,
      timestamp: lastModified || persona?.updated_at,
      trigger: 'publish',
    });
  }
  // Add creation entry
  entries.push({
    rev: 0,
    timestamp: persona?.created_at,
    trigger: cs.origin_id ? 'remix' : 'genesis',
  });

  const dotColor = darkMode ? 'bg-purple-500' : 'bg-purple-400';
  const lineColor = darkMode ? 'border-gray-700' : 'border-gray-300';

  return (
    <div>
      <p className={`text-xs mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Full revision diffs coming in v2 (requires persona versioning table). Currently showing revision log.
      </p>

      <div className="relative">
        {entries.map((entry, i) => (
          <div key={i} className="flex gap-4 mb-6 last:mb-0">
            {/* Timeline dot + line */}
            <div className="flex flex-col items-center">
              <div className={`w-3 h-3 rounded-full ${dotColor} ring-4 ${darkMode ? 'ring-gray-900' : 'ring-white'}`} />
              {i < entries.length - 1 && (
                <div className={`w-px flex-1 border-l-2 ${lineColor} mt-1`} />
              )}
            </div>

            {/* Content */}
            <div className={`flex-1 pb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">Revision {entry.rev}</span>
                <span className={`px-1.5 py-0.5 text-[10px] rounded ${
                  entry.trigger === 'genesis'
                    ? 'bg-green-600/20 text-green-400'
                    : entry.trigger === 'remix'
                      ? 'bg-blue-600/20 text-blue-400'
                      : 'bg-purple-600/20 text-purple-400'
                }`}>
                  {entry.trigger}
                </span>
              </div>
              {entry.timestamp && (
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {new Date(entry.timestamp).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RevisionLog;
